import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { Sparkles } from "lucide-react";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button-variants";

export type AuroraMarkSize = "xs" | "sm";

const MARK_SIZE: Record<AuroraMarkSize, string> = {
  xs: "h-3.5 w-3.5 rounded-[3px] [&_svg]:!h-2.5 [&_svg]:!w-2.5",
  sm: "h-4 w-4 rounded-[4px] [&_svg]:!h-3 [&_svg]:!w-3",
};

/**
 * CAIRE's AI provenance mark: a small chip in the shared aurora gradient
 * (`--gradient-ai`) with a sparkle. Decorative — the label next to it says
 * what the action does.
 */
export function AuroraMark({
  size = "sm",
  className,
}: {
  size?: AuroraMarkSize;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      data-aurora-mark=""
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-white [background-image:var(--gradient-ai)]",
        MARK_SIZE[size],
        className,
      )}
    >
      <Sparkles />
    </span>
  );
}

export type AiActionButtonSize = "xs" | "sm";

const BUTTON_SIZE: Record<AiActionButtonSize, string> = {
  xs: "h-7 gap-1.5 rounded-sm px-2.5 text-xs",
  sm: "h-9 gap-2 rounded-sm px-3 text-sm",
};

export interface AiActionButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type"
> {
  /** `xs` for cards, `sm` for toolbars and headers. */
  size?: AiActionButtonSize;
  /** Render the child (a router link) as the button, keeping the mark. */
  asChild?: boolean;
  type?: "button" | "submit";
}

/**
 * The one look for a labelled AI action a planner starts by choice — every
 * Fråga CAIRE entry point. The Button primitive's `ai` variant (readable card
 * surface, restrained aurora edge) with the `AuroraMark` before the label.
 * Running an optimization keeps `OptimizationAiButton`, the action-filled
 * form of the same aurora cue.
 *
 * @example
 * ```tsx
 * <AiActionButton size="xs" onClick={ask}>Fråga CAIRE</AiActionButton>
 * <AiActionButton asChild><Link to={href}>Fråga CAIRE om analysen</Link></AiActionButton>
 * ```
 */
export const AiActionButton = React.forwardRef<
  HTMLButtonElement,
  AiActionButtonProps
>(
  (
    { size = "sm", asChild = false, className, children, type, ...props },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        data-ai-action="ask"
        className={cn(
          buttonVariants({ variant: "ai" }),
          BUTTON_SIZE[size],
          className,
        )}
        {...props}
      >
        <AuroraMark size={size} />
        <Slottable>{children}</Slottable>
      </Component>
    );
  },
);
AiActionButton.displayName = "AiActionButton";
